import { useState } from 'react'
import { MenuStylePanel } from './MenuStylePanel'
import { MenuTranslationsPanel } from './MenuTranslationsPanel'
import { TableQrPanel } from './TableQrPanel'
import { useAuth } from '../../context/AuthContext'
import { CopyIcon, DownloadIcon, ExternalLinkIcon } from '../icons'

/**
 * The address a customer's phone opens, and a code to print for the table.
 *
 * The code image comes from an external generator rather than a bundled
 * library, a deliberate trade, since the image is needed once, to be printed
 * and stuck to a table. What is sent there is the menu address itself, which
 * is about to be printed and left on a table in public; there is nothing in
 * it to keep.
 *
 * The consequence to know: this panel needs a connection to draw the code.
 * The printed sticker does not, and neither does the menu it opens.
 */
export function QrMenuPanel({ tenantId }: { tenantId: string }) {
  const [copied, setCopied] = useState(false)
  const { hasFeature } = useAuth()

  const url = `${window.location.origin}/m/${tenantId}`
  const qrSrc =
    'https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=10&data=' +
    encodeURIComponent(url)

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access is refused in enough contexts to be worth surviving;
      // the address is on screen and can be copied by hand.
    }
  }

  return (
    <div className="qrm">
      {/* Karekod solda, açıklama ve düğmeler sağında; adres ekranda yazılı
          değil (kullanıcı, 13 Eki 2026), "Adresi kopyala" ile alınıyor. */}
      <div className="qrm__top">
        <div className="qrm__code">
          <img src={qrSrc} alt={`${tenantId} menü karekodu`} width={220} height={220} />
        </div>

        <div className="qrm__info">
          {/* QR Sipariş açıkken bu kodun sipariş vermediği yazıyor (30 Eki
              2026): patron bunu okutup masadan sipariş bekledi. */}
          {hasFeature('qrsiparis') && (
            <p className="qrm__lead qrm__lead--box">
              Bu, <b>genel menü</b> kodu: yalnız menüyü gösterir, sipariş vermez. Masadan sipariş, garson çağırma ve
              hesap için aşağıdaki <b>Masa karekodları</b>nı basın.
            </p>
          )}

          <div className="qrm__buttons">
            <button type="button" className="btn btn--outline" onClick={() => void onCopy()}>
              <CopyIcon />
              <i className="btn__rule" />
              {copied ? 'Kopyalandı ✓' : 'Adresi kopyala'}
            </button>
            <a className="btn btn--outline" href={url} target="_blank" rel="noreferrer">
              <ExternalLinkIcon />
              <i className="btn__rule" />
              Menüyü aç
            </a>
            {/* Opens the generator's own image at full size, which is what gets
                saved and taken to a printer. */}
            <a className="btn btn--outline" href={qrSrc} target="_blank" rel="noreferrer">
              <DownloadIcon />
              <i className="btn__rule" />
              Karekodu indir
            </a>
          </div>

        </div>
      </div>

      {hasFeature('qrsiparis') && (
        <>
          <h4 className="qrm__sub">Masa karekodları</h4>
          <TableQrPanel tenantId={tenantId} />
        </>
      )}

      <h4 className="qrm__sub">Görünüm</h4>
      <MenuStylePanel tenantId={tenantId} />

      <h4 className="qrm__sub">Diller</h4>
      <MenuTranslationsPanel tenantId={tenantId} />
    </div>
  )
}
